import { AppwriteException } from 'appwrite';

export enum LicenseCode {
    BAD_JSON = 'Invalid response format received from server',
    GRACE_PERIOD = 'Your license has expired but is in a grace period',
    GRACE_PERIOD_OVER = 'Grace period has ended, license is no longer valid',
    HTTP_ERROR = 'Server returned an error',
    INVALID_CREDENTIALS = 'Invalid username or password',
    INVALID_RESPONSE = 'Unexpected response from server',
    LICENSE_EXPIRED = 'Your license has expired',
    NETWORK_ERROR = 'Unable to connect to the server',
    NO_SEATS_AVAILABLE = 'No available seats for this license',
    RECONNECT = 'Connection lost, attempting to reconnect',
    SELECT_FAILED = 'Failed to select license',
    SEAT_IN_USE_ON_OTHER_DEVICE = 'This seat is already in use on another device',
    GIT_NOT_INSTALLED = 'Git is not installed'
}

export enum Button {
    RETRY = 'retry',
    SIGN_IN = 'signin',
    SAVE = 'save',
    CONTINUE = 'continue',
    SELECT_LICENSE = 'selectLicense',
    LOGOUT = 'logout',
    RECLAIM = 'reclaim'
}

export type LicenseState = {
    selectedLicense: License | null;
    lastFetched: number;
    inGracePeriodUntil?: number;
    selectedConfirmedAt?: number;
};

export type LicenseTierType = 'Product' | 'Platform' | 'Community' | 'Evaluation';

type BaseLicense = {
    id: string;
    displayName: string;
    seatAvailable: boolean;
    seatCount?: number;
    activeSeats?: number;
    timeoutSeconds?: number;
    expiresAt?: string;
    expired: boolean;
};

export type ProductLicense = BaseLicense & {
    licenseTier: 'Product';
    product_id: string;
    git_url: string;
};

export type NonProductLicense = BaseLicense & {
    licenseTier: Exclude<LicenseTierType, 'Product'>;
    product_id?: string;
    git_url?: string;
};

export type License = ProductLicense | NonProductLicense;

export type LicenseError = {
    title?: string;
    message: string;
    code: string | number;
    status?: number; // HTTP status code when applicable
    timestamp: string; // ISO timestamp when error occurred
    description?: string;
    retryLabel?: string;
    enabledButtons?: Partial<Record<Button, boolean>>;
} | null;

export type LicenseSuccess = {
    success: true;
    licenses: License[];
    deviceId?: string;
};

export type LicenseFailure = {
    success: false;
    error: AppwriteException;
};

export type LicenseResponse = LicenseSuccess | LicenseFailure;

export type ErrorPayload = {
    message?: string;
    code?: string | number;
    description?: string;
};

export type LeaseMetadata = {
    nextHeartbeatInSeconds?: number;
    expiresAt?: string;
    leaseExpiresAt?: string;
};

export type SuccessFlagResponse = {
    success: boolean;
    error?: string | ErrorPayload;
};

export type SelectSeatResponse = SuccessFlagResponse & {
    lease?: LeaseMetadata;
};

export type HeartbeatResponse = SuccessFlagResponse & {
    lease?: LeaseMetadata;
};

export type ReleaseReason =
    | 'logout_license_error'
    | 'logout_selection_screen'
    | 'logout_user_profile'
    | 'license_selection_change'
    | 'app_exit'
    | 'unknown';
